import React, { useEffect, useRef, useState } from 'react';

/**
 * Confirms a start with the exact numbers GET /:id/start-summary reports. In
 * production the admin must tick the checkbox, and these counts are sent back
 * — the backend refuses the start if the batch changed since (stale preview).
 */
const StartConfirmModal = ({ summary, busy, onCancel, onConfirm }) => {
    const isProduction = summary?.requiresConfirmation;
    const [checked, setChecked] = useState(false);
    const cancelRef = useRef(null);

    useEffect(() => {
        cancelRef.current?.focus();
        const onKey = (e) => { if (e.key === 'Escape' && !busy) onCancel(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [busy, onCancel]);

    const series = summary?.seriesCount ?? 0;
    const episodes = summary?.episodeCount ?? 0;

    return (
        <>
            <div className="bsi-overlay" onClick={() => !busy && onCancel()} />
            <div className="bsi-modal" role="dialog" aria-modal="true" aria-labelledby="bsi-start-title">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <span className={`bsi-badge ${isProduction ? 'red' : 'gray'}`} style={{ alignSelf: 'flex-start', textTransform: 'capitalize' }}>
                        {summary?.environment || 'unknown'}
                    </span>
                    <h2 id="bsi-start-title" style={{ margin: 0, fontSize: 21, fontWeight: 700, color: '#fff' }}>
                        {isProduction ? 'Start this import in production?' : 'Start this import?'}
                    </h2>
                    <p className="bsi-muted" style={{ margin: 0, fontSize: 14, lineHeight: 1.5 }}>
                        This creates the series and sends every episode to MediaConvert. Check the numbers match what you expect.
                    </p>
                </div>
                <div>
                    <div className="bsi-kv"><span className="bsi-muted">Source</span><span className="bsi-mono" style={{ color: '#fff', wordBreak: 'break-all', textAlign: 'right' }}>{summary?.source || '—'}</span></div>
                    <div className="bsi-kv"><span className="bsi-muted">Series</span><span className="bsi-title">{series}</span></div>
                    <div className="bsi-kv"><span className="bsi-muted">Episodes to transcode</span><span className="bsi-title">{episodes}</span></div>
                    <div className="bsi-kv"><span className="bsi-muted">Already imported (skipped)</span><span style={{ color: '#fff' }}>{summary?.alreadyImportedCount ?? 0}</span></div>
                </div>
                <div className="bsi-callout blue" style={{ fontSize: 13 }}>
                    New series are created in DRAFT mode. Nothing is visible to viewers until someone publishes it.
                </div>
                {isProduction && (
                    <label style={{ display: 'flex', gap: 12, alignItems: 'center', fontSize: 14, color: '#fff', cursor: 'pointer', minHeight: 40 }}>
                        <input type="checkbox" className="bsi-cb" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
                        I've checked the source and the counts above
                    </label>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                    <button ref={cancelRef} type="button" className="bsi-btn ghost" disabled={busy} onClick={onCancel}>Cancel</button>
                    <button type="button" className="bsi-btn primary" disabled={busy || (isProduction && !checked) || series === 0}
                        onClick={() => onConfirm({ confirm_series_count: series, confirm_episode_count: episodes })}>
                        {busy ? <span className="spinner-border spinner-border-sm" /> : `Start ${series} series · ${episodes} episodes`}
                    </button>
                </div>
                {isProduction && (
                    <p className="bsi-muted" style={{ margin: 0, fontSize: 12, lineHeight: 1.5 }}>
                        If the batch changes before you confirm, the start is refused and these numbers reload.
                    </p>
                )}
            </div>
        </>
    );
};

export default StartConfirmModal;
